import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  ClickAwayListener,
  Grid,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {
  COLOR_ERROR,
  COLOR_GREY_200,
  COLOR_GREY_400,
  COLOR_GREY_600,
  COLOR_PRIMARY_1,
  COLOR_SUCCESS,
  COLOR_TERTIARY_1_100,
  COLOR_TERTIARY_1_800,
  COLOR_WARNING,
} from 'src/colors';
import {
  COLOR_BLUE_BLACK_LIGHT,
  COLOR_PRIMARY,
  exchangeRates,
  userDisplayFriendlyCountryCode,
} from 'src/const';
import {
  abbreviateNumber,
  convertCurrency,
  getCountryDataFromCountryCode,
  trackDiscoveryAIFunnelEvent,
} from 'src/utils/functions';
import {
  getScoreCardTooltipTabledata,
  getScoreColorAndValue,
  marketInSampleFreeMarkets,
  processCompareData,
} from '../helper';
import Checkbox from '@mui/material/Checkbox';
import { InfoTooltip } from 'src/App/HOC/Tooltips';
import TooltipCard from 'src/App/HOC/Cards/TooltipCard';
import { Tooltip, makeStyles } from '@material-ui/core';
import ScoreTooltip from './ScoreTooltip';
import CustomScoreCardTooltip from './CustomScoreCardTooltip';
import market_fallback_image from 'src/App/assets/market_fallback_image.jpg';

const useStylesRoot = makeStyles(theme => ({
  tooltip: {
    padding: 0,
    // width: '400px !important',
    backgroundColor: 'transparent !important',
    margin: '0px !important',
  },
  popperInteractive: {
    width: '350px !important',
  },
  tooltipIcon: {
    position: 'relative',
    width: '100px',
    height: '30px',
    background: '#fff',
    color: '#000',
    borderRadius: 100,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    boxShadow: '0 2px 4px #0004, 0 2px 10px #0002',
    transform: 'translate(-50%, -50%)',
    transition: 'background 0.3s ease, color 0.3s ease',
    '&:hover': {
      background: '#000',
      color: '#fff',
    },
  },
}));

const ScoreCard = ({
  district,
  countryCode,
  country,
  score,
  propertyPrice,
  propertyPriceAppreciation,
  populationGrowth,
  medianHouseholdIncomeGrowth,
  grossRentalYield,
  city,
  market,
  onMouseEnter,
  onMouseLeave,
  marketsToCompareList,
  setMarketsToCompareList,
  minROI,
  maxROI,
  revenue,
  currency,
  annualAppreciation,
  isMarketDataAccessible,
  freeMarketDataKey,
  matches,
  handleOnSampleClick,
  marketGeoType,
  marketLength,
  isFromDiscoveryAi,
  setSelectedMarketInDiscoveryAI,
}) => {
  const imageName =
    district.search(',') != -1
      ? district
      : district +
        ', ' +
        (!city || city == '' || city == 0 ? userDisplayFriendlyCountryCode[countryCode] : city);
  const [openTooltip, setOpenTooltip] = useState(null);
  const rootClass = useStylesRoot();

  const handleTooltipOpen = tooltipName => {
    setOpenTooltip(tooltipName);
  };
  const handleTooltipClose = () => {
    setOpenTooltip(null);
  };

  const imgSrc = `https://airbtics-resource.s3.us-east-2.amazonaws.com/${encodeURIComponent(
    district + ', ' + city
  )}.jpg`;

  const [imgAvailable, setImgAvailable] = useState(true);

  const handleCheckBoxSelection = e => {
    if (e.target.checked === true) {
      if (marketsToCompareList.length < 4) {
        setMarketsToCompareList([
          ...marketsToCompareList,
          processCompareData(market, isMarketDataAccessible, freeMarketDataKey, currency),
        ]);
      }
      // else {
      //   setMarketsToCompareList([...marketsToCompareList.slice(1), processCompareData(market)]);
      // }
    } else {
      setMarketsToCompareList(_ =>
        [...marketsToCompareList].filter(currentMarket => currentMarket[0] !== market.district)
      );
    }
  };

  const checkIfCheckboxIsSelected = () => {
    let isChecked = false;
    for (let selectedMarket of marketsToCompareList) {
      if (selectedMarket[0] === market.district) {
        isChecked = true;
        break;
      }
    }
    return isChecked;
  };

  const handleImageError = () => {
    setImgAvailable(false);
  };

  const getFormattedMarketGeoType = (marketGeoType: string) => {
    if (marketGeoType == 'Mountainous') return '🏔️ Mountainous';
    else if (marketGeoType == 'Beachfront') return '🏖️ Beachfront';
    else if (marketGeoType == 'Urban') return '🏙️ Urban';
    else return '🏡 Suburban';
  };

  return (
    <Box
      sx={{
        width: matches ? '100%' : '300px',
        height: '300px',
        padding: '10px',
        boxShadow:
          'rgba(0, 0, 0, 0.2) 0px 2px 1px -1px, rgba(0, 0, 0, 0.14) 0px 1px 1px 0px, rgba(0, 0, 0, 0.12) 0px 1px 3px 0px',
        marginBottom: '10px',
        filter: market.isDummy ? 'blur(4px)' : 'blur(0px)',
      }}
      onMouseEnter={() => onMouseEnter(market)}
      onMouseLeave={onMouseLeave}
    >
      <Grid container sx={{ flexDirection: 'column', width: '100%' }}>
        {/* Checkbox to add market to compare tab */}
        {!isFromDiscoveryAi && (
          <Box sx={{ height: '0px', width: '100%', display: 'flex', justifyContent: 'flex-end' }}>
            <Tooltip
              arrow
              enterDelay={0}
              enterTouchDelay={0}
              leaveTouchDelay={1000 * 10} // 10 seconds
              title={
                marketsToCompareList.length >= 4
                  ? 'Maximum Comparison Limit of Four Markets has been reached. Please remove a market to add more.'
                  : 'Add to compare.'
              }
            >
              <Box
                sx={{
                  backgroundColor: '#ffff',
                  position: 'relative',
                  top: '6px',
                  left: '-6px',
                  height: '30px',
                  width: '30px',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  borderRadius: '20px',
                }}
              >
                <Checkbox
                  sx={{ padding: 0 }}
                  checked={checkIfCheckboxIsSelected()}
                  onChange={handleCheckBoxSelection}
                  disabled={marketsToCompareList.length >= 4 && !checkIfCheckboxIsSelected()}
                />
              </Box>
            </Tooltip>
          </Box>
        )}
        {isFromDiscoveryAi && (
          <Box sx={{ height: '0px', width: '100%', display: 'flex', justifyContent: 'flex-end' }}>
            <Tooltip
              arrow
              enterDelay={0}
              enterTouchDelay={0}
              leaveTouchDelay={1000 * 10} // 10 seconds
              title={'Go To Market'}
            >
              <Box
                sx={{
                  backgroundColor: '#ffff',
                  position: 'relative',
                  top: '6px',
                  left: '-6px',
                  height: '30px',
                  width: '30px',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  borderRadius: '20px',
                  cursor: 'pointer',
                }}
                onClick={() => {
                  trackDiscoveryAIFunnelEvent('clickedOnGTM');
                  handleOnSampleClick();
                }}
              >
                <OpenInNewIcon />
              </Box>
            </Tooltip>
          </Box>
        )}
        {/* Market Geography Type */}
        {marketGeoType && (
          <Box sx={{ height: '0px', width: '100%', display: 'flex', justifyContent: 'flex-start' }}>
            <Box
              sx={{
                backgroundColor: '#ffff',
                position: 'relative',
                top: '6px',
                left: '6px',
                height: '30px',
                width: 'auto',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                borderRadius: '20px',
                padding: '0 10px',
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 400,
                  color: COLOR_BLUE_BLACK_LIGHT,
                }}
              >
                {getFormattedMarketGeoType(marketGeoType)}
              </Typography>
            </Box>
          </Box>
        )}
        {/* Image */}
        <Grid
          item
          sx={{
            height: '100px',
            width: '100%',
            borderRadius: '10px',
            overflow: 'hidden',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {imgAvailable ? (
            <img
              src={imgSrc}
              onError={handleImageError}
              alt="Market Grad Image"
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          ) : (
            <div
              style={{
                backgroundImage: `url(${market_fallback_image})`,
                backgroundSize: '100%',
                height: '100%',
                width: '100%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <Typography
                variant="body1"
                sx={{
                  fontWeight: 500,
                  maxWidth: '170px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  color: 'white',
                }}
              >
                {imageName}
              </Typography>
            </div>
          )}
        </Grid>

        {/* Name and score rating */}
        <Grid item sx={{ height: '50px', width: '100%', padding: '0px 10px' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', marginTop: '5px', gap: 1 }}>
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <Typography
                variant="body1"
                sx={{
                  fontWeight: 500,
                  maxWidth: '140px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {isFromDiscoveryAi
                  ? district
                  : district
                      .toLowerCase()
                      .includes(userDisplayFriendlyCountryCode[countryCode].toLowerCase())
                  ? district.replace(`, ${userDisplayFriendlyCountryCode[countryCode]}`, '')
                  : district}
                ,
              </Typography>
              <span
                style={{
                  color: COLOR_GREY_600,
                  fontFamily: 'Roboto',
                  fontSize: '15px',
                  paddingTop: '5px',
                }}
              >
                {!city || city == '' || city == 0
                  ? userDisplayFriendlyCountryCode[countryCode]
                  : city}
              </span>
              <Box sx={{ height: '20px', width: '30px', overflow: 'hidden', marginTop: '2px' }}>
                <img
                  src={`https://flagcdn.com/w80/${countryCode?.toLowerCase()}.png`}
                  srcSet={`https://flagcdn.com/w80/${countryCode?.toLowerCase()}.png 2x`}
                  alt="Country flag"
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              </Box>
            </Box>
            <Box>
              {/* Score circle */}
              <ClickAwayListener onClickAway={handleTooltipClose}>
                <Tooltip
                  interactive
                  classes={rootClass}
                  open={openTooltip == 'score'}
                  onOpen={() => {
                    handleTooltipOpen('score');
                  }}
                  onClose={handleTooltipClose}
                  enterDelay={0}
                  enterTouchDelay={0}
                  leaveTouchDelay={1000 * 10} // 10 seconds
                  title={
                    !isFromDiscoveryAi ? (
                      <Card
                        sx={{
                          height: '370px',
                          width: '330px',
                          padding: '10px !important',
                          position: 'relative',
                        }}
                      >
                        <CardContent
                          sx={{
                            zIndex: 999999,
                            height: '100%',
                            width: '100%',
                            padding: '0px',
                          }}
                        >
                          <ScoreTooltip market={market} currency={currency} />
                        </CardContent>
                      </Card>
                    ) : (
                      <></>
                    )
                  }
                >
                  <Box
                    sx={{
                      position: 'relative',
                      display: 'inline-flex',
                      '&:hover': { cursor: 'pointer' },
                    }}
                  >
                    <CircularProgress
                      variant="determinate"
                      value={getScoreColorAndValue(score, maxROI, minROI).score}
                      thickness={5}
                      size={40}
                      sx={{
                        '& .MuiCircularProgress-circle': {
                          strokeLinecap: 'round',
                        },
                        color: getScoreColorAndValue(score, maxROI, minROI).color,
                      }}
                    />
                    <Box
                      sx={{
                        top: 0,
                        left: 0,
                        bottom: 0,
                        right: 0,
                        position: 'absolute',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: '12px',
                          fontWeight: 500,
                          filter: isFromDiscoveryAi ? 'blur(3.5px) ' : 'blur(0px)',
                        }}
                      >
                        {score.toFixed(1)}x
                      </Typography>
                    </Box>
                  </Box>
                </Tooltip>
              </ClickAwayListener>
            </Box>
          </Box>
        </Grid>

        {/* Metrices */}
        <Grid item sx={{ height: '90px', width: '100%' }}>
          <Grid container sx={{ height: '100%', backgroundColor: '#f8f8f8', borderRadius: '20px' }}>
            {/* Yearly revenue */}
            <Grid item sm={4} sx={{ height: '100%', padding: '15px 5px 0 10px' }}>
              <ClickAwayListener onClickAway={handleTooltipClose}>
                <Tooltip
                  interactive
                  classes={rootClass}
                  open={openTooltip == 'revenue'}
                  onOpen={() => {
                    handleTooltipOpen('revenue');
                  }}
                  onClose={handleTooltipClose}
                  enterDelay={0}
                  enterTouchDelay={0}
                  leaveTouchDelay={1000 * 10} // 10 seconds
                  title={
                    <Card
                      sx={{ height: '150px', width: '370px', padding: '5px 10px !important' }}
                      onClick={e => {
                        e.stopPropagation();
                      }}
                    >
                      <CardContent
                        style={{ zIndex: 999999, height: '100%', width: '100%', padding: '0px' }}
                      >
                        <CustomScoreCardTooltip
                          isMarketDataAccessible={
                            !isMarketDataAccessible &&
                            !marketInSampleFreeMarkets(market, freeMarketDataKey)
                          }
                          marketName={district}
                          col1Title="Regulation"
                          col1Val={'Coming Soon'}
                          col2Title="Occupancy Rate"
                          col2Val={market.occupancy_rate + '%'}
                          col3Title="Daily Rate"
                          col3Val={
                            exchangeRates[currency].symbol +
                            abbreviateNumber(convertCurrency(currency, market.adr))
                          }
                          handleOnSampleClick={handleOnSampleClick}
                          countryCode={countryCode}
                          isFromDiscoveryAi={isFromDiscoveryAi}
                        />
                      </CardContent>
                    </Card>
                  }
                >
                  <Box
                    sx={{
                      color: openTooltip == 'revenue' ? COLOR_PRIMARY_1 : '#000',
                      '&:hover': { cursor: 'pointer' },
                    }}
                  >
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: 500,
                        textAlign: 'center',
                        filter: isFromDiscoveryAi ? 'blur(3.5px) ' : 'blur(0px)',
                      }}
                      onClick={isFromDiscoveryAi ? handleOnSampleClick : null}
                    >
                      {exchangeRates[currency].symbol +
                        abbreviateNumber(convertCurrency(currency, revenue))}
                    </Typography>
                    <Typography
                      style={{
                        fontSize: '12px',
                        textAlign: 'center',
                      }}
                    >
                      Yearly Revenue
                    </Typography>
                  </Box>
                </Tooltip>
              </ClickAwayListener>
            </Grid>
            {/* Net Rental Yield */}
            <Grid
              item
              sm={4}
              sx={{
                height: '100%',
                borderLeft: '1px solid grey',
                borderRight: '1px solid grey',
                padding: '15px 5px 0 10px',
              }}
            >
              <ClickAwayListener onClickAway={handleTooltipClose}>
                <Tooltip
                  interactive
                  classes={rootClass}
                  open={openTooltip == 'yield'}
                  onOpen={() => {
                    handleTooltipOpen('yield');
                  }}
                  onClose={handleTooltipClose}
                  enterDelay={0}
                  enterTouchDelay={0}
                  leaveTouchDelay={1000 * 10} // 10 seconds
                  title={
                    <Card sx={{ height: '150px', width: '370px', padding: '5px 10px !important' }}>
                      <CardContent
                        style={{ zIndex: 999999, height: '100%', width: '100%', padding: '0px' }}
                      >
                        <CustomScoreCardTooltip
                          isMarketDataAccessible={
                            !isMarketDataAccessible &&
                            !marketInSampleFreeMarkets(market, freeMarketDataKey)
                          }
                          marketName={district}
                          col1Title="Property Price"
                          col1Val={
                            !isMarketDataAccessible &&
                            !marketInSampleFreeMarkets(market, freeMarketDataKey)
                              ? '$ 100k'
                              : exchangeRates[currency].symbol +
                                abbreviateNumber(convertCurrency(currency, market.property_price))
                          }
                          col2Title="Average Revenue"
                          col2Val={
                            !isMarketDataAccessible &&
                            !marketInSampleFreeMarkets(market, freeMarketDataKey)
                              ? '$ 100k'
                              : exchangeRates[currency].symbol +
                                abbreviateNumber(convertCurrency(currency, market.revenue))
                          }
                          col3Title="Average Expenses"
                          col3Val={
                            !isMarketDataAccessible &&
                            !marketInSampleFreeMarkets(market, freeMarketDataKey)
                              ? '$ 100k'
                              : exchangeRates[currency].symbol +
                                abbreviateNumber(convertCurrency(currency, market.average_expense))
                          }
                          handleOnSampleClick={handleOnSampleClick}
                          countryCode={countryCode}
                        />
                      </CardContent>
                    </Card>
                  }
                >
                  <Box
                    sx={{
                      color: openTooltip == 'yield' ? COLOR_PRIMARY_1 : '#000',
                      '&:hover': { cursor: 'pointer' },
                    }}
                  >
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: 500,
                        textAlign: 'center',
                        filter:
                          !isMarketDataAccessible &&
                          !marketInSampleFreeMarkets(market, freeMarketDataKey)
                            ? 'blur(3.5px) '
                            : 'blur(0px)',
                      }}
                      onClick={
                        !isMarketDataAccessible &&
                        !marketInSampleFreeMarkets(market, freeMarketDataKey)
                          ? handleOnSampleClick
                          : null
                      }
                    >
                      {!isMarketDataAccessible &&
                      !marketInSampleFreeMarkets(market, freeMarketDataKey)
                        ? '100'
                        : grossRentalYield}{' '}
                      %
                    </Typography>
                    <Typography style={{ fontSize: '12px', textAlign: 'center' }}>
                      Net Rental Yield
                    </Typography>
                  </Box>
                </Tooltip>
              </ClickAwayListener>
            </Grid>
            {/* Annual Appreciation */}
            <Grid item sm={4} sx={{ height: '100%', padding: '15px 0 0 10px' }}>
              <ClickAwayListener onClickAway={handleTooltipClose}>
                <Tooltip
                  interactive
                  classes={rootClass}
                  open={openTooltip == 'annualAppreciation'}
                  onOpen={() => {
                    handleTooltipOpen('annualAppreciation');
                  }}
                  onClose={handleTooltipClose}
                  enterDelay={0}
                  enterTouchDelay={0}
                  leaveTouchDelay={1000 * 10} // 10 seconds
                  title={
                    <Card sx={{ height: '160px', width: '370px', padding: '5px 10px !important' }}>
                      <CardContent
                        style={{ zIndex: 999999, height: '100%', width: '100%', padding: '0px' }}
                      >
                        <CustomScoreCardTooltip
                          isMarketDataAccessible={
                            !isMarketDataAccessible &&
                            !marketInSampleFreeMarkets(market, freeMarketDataKey)
                          }
                          marketName={district}
                          col1Title="Property Price Change"
                          col1Val={
                            !isMarketDataAccessible &&
                            !marketInSampleFreeMarkets(market, freeMarketDataKey)
                              ? '100%'
                              : market.property_price_change + '%'
                          }
                          col1TooltipObj={{
                            rank: market.property_price_change_rank,
                            totalMarkets: marketLength,
                            nationalAverageComparision: market.prop_price_change_diff_with_nat_avg,
                          }}
                          col2Title="Polulation Change"
                          col2Val={
                            !isMarketDataAccessible &&
                            !marketInSampleFreeMarkets(market, freeMarketDataKey)
                              ? '100%'
                              : market.population_change + '%'
                          }
                          col2TooltipObj={{
                            rank: market.population_change_rank,
                            totalMarkets: marketLength,
                            nationalAverageComparision: market.population_change_diff_with_nat_avg,
                          }}
                          col3Title="Income Change"
                          col3Val={
                            !isMarketDataAccessible &&
                            !marketInSampleFreeMarkets(market, freeMarketDataKey)
                              ? '100%'
                              : market.income_change + '%'
                          }
                          col3TooltipObj={{
                            rank: market.income_change_rank,
                            totalMarkets: marketLength,
                            nationalAverageComparision: market.income_change_diff_with_nat_avg,
                          }}
                          handleOnSampleClick={handleOnSampleClick}
                          countryCode={countryCode}
                        />
                      </CardContent>
                    </Card>
                  }
                >
                  <Box
                    sx={{
                      color: openTooltip == 'annualAppreciation' ? COLOR_PRIMARY_1 : '#000',
                      '&:hover': { cursor: 'pointer' },
                    }}
                  >
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: 500,
                        textAlign: 'center',
                        filter:
                          !isMarketDataAccessible &&
                          !marketInSampleFreeMarkets(market, freeMarketDataKey)
                            ? 'blur(3.5px) '
                            : 'blur(0px)',
                      }}
                      onClick={
                        !isMarketDataAccessible &&
                        !marketInSampleFreeMarkets(market, freeMarketDataKey)
                          ? handleOnSampleClick
                          : null
                      }
                    >
                      {!isMarketDataAccessible &&
                      !marketInSampleFreeMarkets(market, freeMarketDataKey)
                        ? '100'
                        : annualAppreciation}
                      %
                    </Typography>
                    <Typography style={{ fontSize: '12px', textAlign: 'center' }}>
                      Annual Appreciation
                    </Typography>
                  </Box>
                </Tooltip>
              </ClickAwayListener>
            </Grid>
          </Grid>
        </Grid>

        {/* View market Button */}
        <Grid item sx={{ height: '70px', width: '100%' }}>
          <Button
            sx={{
              width: '100%',
              marginTop: '10px',
              background: COLOR_PRIMARY + '22',
              color: COLOR_PRIMARY,
              borderRadius: '4px',
              '&:hover': {
                color: '#fff',
                background: COLOR_PRIMARY,
              },
            }}
            onClick={() => {
              if (!isFromDiscoveryAi) {
                const urlFormattedcountry = country ? country.toLowerCase().replace(' ', '-') : '0';
                const url = `/airbnb-data/${urlFormattedcountry}/${city.toLowerCase()}/${district.toLowerCase()}`;
                window.open(url, '_blank');
              } else {
                setSelectedMarketInDiscoveryAI(district);
                trackDiscoveryAIFunnelEvent('clickedViewDetailsOnMarketCards');
                try {
                  const element = document.getElementById('detailedAIView');
                  if (element) {
                    element.scrollIntoView({ behavior: 'smooth' });
                  }
                } catch (e) {
                  console.log(e);
                }
              }
            }}
          >
            <Typography>{isFromDiscoveryAi ? 'View Details' : 'View Market'}</Typography>
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ScoreCard;
